import React, {useEffect, useRef, useState} from 'react';
import styles from './StatusInfo.module.scss';
import {Button, Input, Modal, Notify, Select, Status} from "uikit";
import {StatusInfoSchema} from "./StatusInfo.schema";
import {StatusInfoPropsInterface} from "./StatusInfo.props.interface";
import ArrowIcon from "@/assets/images/icons/12/arrow.svg";
import TagIcon from "@/assets/images/icons/12/tag.svg";
import {GeneralMethods} from "@/views/Admin/Events/Event/General/General.methods";
import {Link, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import ModalTagIcon from "@/assets/images/icons/TagIcon.svg";
import CrossIcon from "@/assets/images/icons/12/close.svg";
import MultiSelect from "@/UIKit/component/multiselect/MultiSelect";
import PencilIcon from "@/assets/images/icons/16/pencil.svg";
import {Russian} from "flatpickr/dist/l10n/ru";
import Flatpickr from "react-flatpickr";

const config = {
    dateFormat: "m-d",
    monthSelectorType: "static",
    allowInput: true,
    disableMobile: true,
    locale: Russian,
    position: "auto",
    minDate: "today"
}

const StatusInfo = ({data, onChange, handleChangeFields}: StatusInfoPropsInterface) => {
    const [content, setContent] = useState({...StatusInfoSchema});

    const permissionsType = data.eventType === 'courses' ? data?.permissions?.courses : data?.permissions?.events;
    const statusMapping = {0: 'warning', 1: 'success', 2: 'danger', 4: 'secondary', 6: 'tertiary'}
    const ageRange = {'any': 'не отображать', '0': '0+', '6': '6+', '12': '12+', '16': '16+', '18': '18+'};
    const initialTagsRef = useRef(data.tags)

    const params: { id: any } = useParams();
    const {t} = useTranslation('common');

    const {
        archiveEvent,
        getTags,
        copyEvent,
        createTag,
        cancelEvent
    } = GeneralMethods;

    useEffect(() => {
        if (content.isTagModal) {
            initialTagsRef.current = data.tags;
        }
    }, [content.isTagModal]);

    const handleArchiveEvent = async () => {
        let response;
        try {
            response = await archiveEvent(data.eventType, params.id);
            if (response.data.success) {
                onChange(prev => ({...prev,
                    fields: {...prev.fields,
                        status: {...prev.fields.status, value: 'В архиве'},
                        statusCode: {...prev.fields.statusCode, value: 6}}})
                )
                // @ts-ignore
                showTopMessage('success', 'small' , response.data.message);
            }
        } catch (err) {
            // @ts-ignore
            showTopMessage('error', 'small' , 'Произошла ошибка');
        }
    }

    const handleAccessCopy = () => {
        if (permissionsType['do-copy']) {
            return {
                'copy': <div onClick={() => setContent(prev => ({...prev, isCopyModal: true}))}>
                    {data.eventType === 'events' ? 'Создать копию события' : 'Создать копию курса'}
                </div>
            }
        }
    }

    const handleCopyEvent = async (dateStart) => {
        let response, body, date;
        date = dateStart ? new Date(dateStart).toLocaleString('sv-SE', {
            hour12: false
        }).replace(/\b(\d)\b/g, '0$1').replace(' ', 'T') + data.utc : null;
        body = {
            date_start: date
        }
        try {
            response = await copyEvent(data.eventType, params.id, body);
            if (response.data.success) {
                onChange(prev => ({...prev,
                    fields: {...prev.fields,
                        eventCopyId: {...prev.fields.eventCopyId, value: response.data['event_id_new'], label: 'Копия успешно создана'}}}))
            }
        } catch (err) {
            // @ts-ignore
            showTopMessage('error', 'small' , 'Произошла ошибка при копировании');
        }
    }

    const renderActionsBtns = () => {
        if (data.fields.statusCode.value === 6) return null

        if (data.fields.statusCode.value === 1 && permissionsType['do-cancel']) {
            return {
                'cancel': data.eventType === 'events'
                    ? <a href={`/admin/cancel_event/${params.id}/`} style={{color: 'inherit'}}>Отменить событие</a>
                    : <div onClick={() => setContent(prev => ({...prev, isCancelModal: true}))}>Отменить курс</div>
            }
        } else if (data.fields.statusCode.value !== 1 && permissionsType['do-archive']) {
            return { 'archive': <div onClick={handleArchiveEvent}>Убрать в архив</div> }
        }
    }

    const handleCategory = (category) => {
        handleChangeFields('category', category)
    }

    const handleRenderCategory = () => {
        if (permissionsType['change-category']) {
            return <Select
                size={'small'}
                direction={['bottom', 'center']}
                value={Number(data.fields.category.value)}
                data={data.constants?.categories}
                onChange={(e) => handleCategory(e)}>
                <div className={styles.dropdown}>
                    <p>{data.constants?.categories[Number(data.fields.category.value)]}</p>
                    <ArrowIcon/>
                </div>
            </Select>
        } else {
            return <div className={styles.dropdown}>
                <p>{data.constants?.categories[Number(data.fields.category.value)]}</p>
            </div>
        }
    }

    const handleAge = (age) => {
        handleChangeFields('age', age)
    }

    const handleGetTags = async () => {
        let response
        try {
            response = await getTags()
            if (response) {
                setContent(prev => ({...prev, tagsConst: response}))
            }
        } catch (err) {
            // @ts-ignore
            showTopMessage('error', 'small' , `Произошла ощибка при получении тегов`);
        }
    }

    const handleSetTags = (tags) => {
        onChange(prev => ({...prev,  tags: tags}))
    }

    const handleTags = () => {
        handleChangeFields('tags', data.tags)
        setContent(prev => ({...prev, isTagModal: false}))
    }

    const handleCreateTag = async (title) => {
        try {
            const response = await createTag(title)
            if (response) {
                return response
            }
        } catch ({response}) {
            // @ts-ignore
            showTopMessage('error', 'small' , response.data.errors[0]?.detail);
        }
    }

    const handleCancelCourse = async () => {
        let response;
        try {
            response = await cancelEvent(params.id);
            if ([200, 201].includes(response.status)) {
                // @ts-ignore
                showTopMessage('success', 'small' , 'Курс отменен');
                onChange(prev => ({...prev,
                    isCancelModal: false,
                    fields: {...prev.fields,
                        status: {...prev.fields.status, value: 'Отменено'},
                        statusCode: {...prev.fields.statusCode, value: 2}}})
                )
            }
        } catch (err) {
            // @ts-ignore
            showTopMessage('error', 'small' , 'Произошла ошибка');
        }
    }

    const handleConvertDate = (date: string | Date) => {
        return new Date(date as string).toLocaleString('ru-RU', {day: 'numeric', month: 'numeric', year: 'numeric'});
    }

    return (
        <div className={styles.tags}>
            <div className={styles.state}>
                <div className={styles.status}>
                    {data.fields.statusCode.value !== null ?
                        <Status
                            size={'small'}
                            data={{
                                ...handleAccessCopy(),
                                ...renderActionsBtns(),
                            }}
                            type={data.fields.statusCode.value ? statusMapping[Number(data.fields.statusCode.value)] : 'warning'}
                            direction={['bottom', 'center']}
                            onChange={(key) => console.log(key)}
                        >
                            {data.fields.status.value}
                        </Status> : null
                    }
                </div>
                {data.constants ? handleRenderCategory() : null}
            </div>
            <div className={styles.age}>
                <small>
                    {t('age')}
                </small>
                {permissionsType['change-age']
                    ? <Select
                        size={'small'}
                        direction={['bottom', 'center']}
                        value={String(data.fields.age.value)}
                        data={ageRange}
                        onChange={(e) => handleAge(e)}>
                        <div className={styles.dropdown}>
                            <p>{ageRange[String(data.fields.age.value)]}</p>
                            <ArrowIcon/>
                        </div>
                    </Select>
                    : <div className={styles.dropdown}>
                        <p>{ageRange[String(data.fields.age.value)]}</p>
                    </div>
                }
            </div>
            <div className={styles.category}>
                <Button
                    size={'small'}
                    theme={'default'}
                    type={'secondary'}
                    disabled={!permissionsType['change-tags']}
                    onClick={async () => {
                        await handleGetTags()
                        setContent(prev => ({...prev, isTagModal: true}))
                    }}>
                    <TagIcon/>
                    <span>Добавить теги</span>
                </Button>
                <div className={styles.tag}>
                    {data.tags.map((tag: { id: string; title: string }) => (
                        <p key={tag.id}>{tag.title}{data.tags.length > 1 ? ', ' : ''}</p>
                    ))
                    }
                </div>
            </div>
            {/*tags modal*/}
            <div className={styles.tagsModal}>
                <Modal
                    style={{padding: '16px', paddingLeft: '64px', maxWidth: '720px', width: '100%'}}
                    className={styles.modal}
                    active={content.isTagModal}
                    onActive={ (e: boolean) => {
                        setContent(prev => ({...prev, isTagModal: e}))
                    }}
                    onMissClick={(event) => console.log(event)}
                    onEscape={(event) => console.log(event)}
                >
                    <div>
                        <p className={styles.modalSubtitle}>{data.fields.type.value}</p>
                        <h6 className={styles.modalTitle}> {data.fields.title.value}</h6>
                    </div>
                    <div className={styles.modalTagIcon}>
                        <ModalTagIcon />
                    </div>
                    <div
                        className={styles.modalCross}
                        onClick={() => setContent(prev => ({...prev, isTagModal: false}))}
                    >
                        <CrossIcon/>
                    </div>
                    <div className={styles.tagsInput}>
                        <p className={styles.tagsLabel}>Теги (сохраняйте при помощи Enter или Space)</p>
                        <MultiSelect
                            initialTags={data.tags}
                            onChange={(tags) => handleSetTags(tags)}
                            options={content.tagsConst ? content.tagsConst : []}
                            createTag={handleCreateTag}
                        />
                    </div>
                    <p className={styles.tagsSub}>* {t('tag_warning')}</p>
                    <div className={styles.modalControl}>
                        <Button
                            size={'small'}
                            theme={'ghost'}
                            type={'tertiary'}
                            onClick={() => {
                                setContent(prev => ({...prev, isTagModal: false}))
                                onChange(prev => ({...prev, tags: initialTagsRef.current}))
                            }}
                        >
                            <span>{t('cancel')}</span>
                        </Button>
                        <Button
                            size={'small'}
                            theme={'default'}
                            type={'primary'}
                            onClick={handleTags}
                        >
                            <span>{t('ready')}</span>
                        </Button>
                    </div>
                </Modal>
            </div>
            {/*copy modal*/}
            <Modal
                style={{padding: '32px', maxWidth: '450px', width: '100%'}}
                className={styles.modal}
                active={content.isCopyModal}
                onActive={(e: boolean) => setContent(prev => ({...prev, isCopyModal: e}))}
                onMissClick={(event) => console.log(event)}
                onEscape={(event) => console.log(event)}
            >
                <div>
                    <p className={styles.modalSubtitle}>{data.fields.type.value}</p>
                    <h6 className={styles.modalTitle}> {data.fields.title.value}</h6>
                </div>
                {data.fields.dateStart.value && !data.fields.eventCopyId.value
                    ? <Flatpickr
                        options={{...config}}
                        onChange={([date]: Date[]) => setContent(prev => ({ ...prev, copyDateStart: date }))}
                        value={content.copyDateStart ? new Date(String(content.copyDateStart)) : ''}
                        render={({value, defaultValue}, ref) => <div className={styles.date} ref={ref}>
                            <Input
                                onChange={() => null}
                                size={"large"}
                                value={content.copyDateStart ? handleConvertDate(content.copyDateStart) : null}
                                placeholder={`${t('date_start')}`}
                            />
                        </div>}
                    /> : null
                }
                {!data.fields.dateStart.value && !data.fields.eventCopyId.value ? <p>{t('make_copy')}?</p> : null}
                {data.fields.eventCopyId.label ? <Notify type={"success"} icon={true}>{data.fields.eventCopyId.label}</Notify> : null}
                <div
                    className={styles.modalCross}
                    onClick={() => setContent(prev => ({...prev, isCopyModal: false}))}
                >
                    <CrossIcon/>
                </div>
                <div className={styles.modalControl}>
                    {!data.fields.eventCopyId.value
                        ? <>
                            <Button
                                size={'small'}
                                theme={'ghost'}
                                type={'tertiary'}
                                onClick={() => setContent(prev => ({...prev, isCopyModal: false}))}
                            >
                                <span>{t('cancel')}</span>
                            </Button>
                            <Button
                                size={'small'}
                                theme={'default'}
                                type={'primary'}
                                onClick={() => handleCopyEvent(content.copyDateStart ? content.copyDateStart : null )}
                            >
                                <span>{t('make_copy')}</span>
                            </Button>
                        </>
                        :  <>
                            <Link
                                to={data.eventType === 'events' ? `/admin/settings/${data.fields.eventCopyId.value}/` : `/admin/courses/${data.fields.eventCopyId.value}/lessons`}
                                target={'_blank'}
                            >
                                <Button
                                    size={'small'}
                                    theme={'outline'}
                                    type={'primary'}
                                    onClick={(e) => console.log(e)}>
                                    {t('settings')}
                                </Button>
                            </Link>
                            <Link to={`/admin/editor/${data.fields.eventCopyId.value}/`} target={'_blank'}>
                                <Button
                                    size={'small'}
                                    theme={'default'}
                                    type={'primary'}
                                    onClick={(e) => console.log(e)}>
                                    <PencilIcon />
                                    {t('edit')}
                                </Button>
                            </Link>
                        </>
                    }
                </div>
            </Modal>
            {/*course cancel modal*/}
            <Modal
                style={{padding: '32px', maxWidth: '450px', width: '100%'}}
                className={styles.modal}
                active={content.isCancelModal}
                onActive={(e: boolean) => setContent(prev => ({...prev, isCancelModal: e}))}
                onMissClick={(event) => console.log(event)}
                onEscape={(event) => console.log(event)}
            >
                <div>
                    <p className={styles.modalSubtitle}>{data.fields.type.value}</p>
                    <h6 className={styles.modalTitle}> {data.fields.title.value}</h6>
                </div>
                <p>{t('sure')}?</p>
                <div
                    className={styles.modalCross}
                    onClick={() => setContent(prev => ({...prev, isCancelModal: false}))}
                >
                    <CrossIcon/>
                </div>
                <div className={styles.modalControl}>
                    <Button
                        size={'small'}
                        theme={'ghost'}
                        type={'tertiary'}
                        onClick={() => setContent(prev => ({...prev, isCancelModal: false}))}
                    >
                        <span>{t('no')}</span>
                    </Button>
                    <Button
                        size={'small'}
                        theme={'default'}
                        type={'danger'}
                        onClick={handleCancelCourse}
                    >
                        <span>{t('yes')}</span>
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default StatusInfo;
